import { type JSX } from "react";
import type { StyleProps } from "@chakra-ui/react";
import { Box, Text, Stack } from "@chakra-ui/react";
import { Icon } from "../icon/Icon";
import { Container } from "components/container/Container";

type SnackbarType = "success" | "warning" | "error";

export interface SnackbarProps extends StyleProps {
  type: SnackbarType;
  onClick?: () => void;
  onClose?: () => void;
  buttonText?: string;
  children?: JSX.Element | string;
}

const snackbarConfig = {
  success: {
    Icon: <Icon name="status/Color/Success" fill="success.fg" />,
    bg: "success.default",
  },
  warning: {
    Icon: <Icon name="status/Color/Alert" fill="warning.fg" />,
    bg: "warning.default",
  },
  error: {
    Icon: <Icon name="status/Color/Error" fill="error.fg" />,
    bg: "error.default",
  },
};

/**
 * Provides lightweight, transient feedback after a user action. Snackbars
 * communicate the outcome of an operation without interrupting the user's
 * workflow. For errors that block a workflow, use Workflow Error Banner
 * instead.
 */
export const Snackbar = ({
  type,
  onClick,
  onClose,
  buttonText,
  children,
}: SnackbarProps) => {
  return (
    <Container
      data-cy={`snackbar-${type}`}
      padding="4"
      borderRadius={["none", "none", "sm"]}
      minWidth={["100%", "100%", "376px"]}
      maxWidth={["100%", "100%", "480px"]}
      bg={snackbarConfig[type].bg}
    >
      <Stack direction="row" spacing="2" alignItems="flex-start">
        <Box width="16px" paddingTop="0.5">
          {snackbarConfig[type].Icon}
        </Box>
        <Stack
          direction={["column", "column", "row"]}
          alignItems="flex-start"
          gap="4"
          width="100%"
          justifyContent={["flex-start", "space-between", "space-between"]}
        >
          <Text textStyle="base">{children}</Text>
          {onClick && buttonText ? (
            <Text
              textStyle="buttonLinkS"
              cursor={"pointer"}
              color="highlight.fg"
              paddingLeft={[0, 0, "2"]}
              onClick={onClick}
            >
              {buttonText}
            </Text>
          ) : (
            <Icon
              name="actions/Close/Small"
              cursor="pointer"
              onClick={onClose}
            />
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
